import { TopolRatingBlock } from "@/schemas/template/blocks/ratingBlockSchema";
import calcWidth from "./helpers";
import { v4 as uuidv4 } from "uuid";
import { templateSettings } from "@/store/templateSetting";

const START_VALUE = 0;
const COUNT = 11;

function resolveDefaults(state: any, group?: string): Record<string, any> {
  const baseAttr = state?.mjml?.attributes?.["mj-rating"];
  if (group) {
    return {
      ...(templateSettings["mj-rating"]?.[
        group as keyof (typeof templateSettings)["mj-rating"]
      ] as Record<string, any> | undefined),
      ...((baseAttr &&
        typeof baseAttr === "object" &&
        (baseAttr as any)[group]) as Record<string, any> | undefined),
    };
  }
  // Flat top-level attrs (mirror block.attributes)
  const settings = templateSettings["mj-rating"] as
    | Record<string, any>
    | undefined;
  const flatTemplate: Record<string, any> = {};
  if (settings) {
    for (const key of Object.keys(settings)) {
      const v = settings[key];
      if (typeof v !== "object" || v === null) {
        flatTemplate[key] = v;
      }
    }
  }
  const flatBase: Record<string, any> = {};
  if (baseAttr && typeof baseAttr === "object") {
    for (const key of Object.keys(baseAttr as Record<string, any>)) {
      const v = (baseAttr as Record<string, any>)[key];
      if (typeof v !== "object" || v === null) {
        flatBase[key] = v;
      }
    }
  }
  return { ...flatTemplate, ...flatBase };
}

export default {
  getData(
    state: any,
    props: { sectionIndex: number; columnIndex: number }
  ): TopolRatingBlock {
    const children = [];
    for (let i = 0; i < COUNT; i++) {
      const value = START_VALUE + i;
      children.push({
        tagName: "mj-rating-segment" as const,
        attributes: {
          label: String(value),
          src: "",
          alt: String(value),
          href: "",
          value,
        },
      });
    }

    const attrDefaults = resolveDefaults(state);
    const questionDefaults = resolveDefaults(state, "questionAttributes");
    const captionsDefaults = resolveDefaults(state, "captionsAttributes");
    const segmentsDefaults = resolveDefaults(state, "segmentsAttributes");
    const imageLabelDefaults = resolveDefaults(state, "imageLabelAttributes");

    return {
      uid: uuidv4(),
      tagName: "mj-rating",
      attributes: {
        align: attrDefaults.align ?? "center",
        padding: attrDefaults.padding ?? "10px 10px 10px 10px",
        containerWidth: calcWidth(
          state.mjml.children[0].attributes["containerWidth"],
          state.mjml.children[0].children[props.sectionIndex].children[
            props.columnIndex
          ].attributes["width"]
        ),
      },
      questionText: "How likely are you to recommend us?",
      questionAttributes: {
        show: true,
        color: questionDefaults.color ?? "#1a1a1a",
        "font-size": questionDefaults["font-size"] ?? "18px",
        "font-family":
          questionDefaults["font-family"] ?? "Helvetica, Arial, sans-serif",
        "font-weight": questionDefaults["font-weight"] ?? "600",
        padding: questionDefaults.padding ?? "0px 0px 12px 0px",
        ...(questionDefaults["font-style"]
          ? { "font-style": questionDefaults["font-style"] }
          : {}),
        ...(questionDefaults["text-decoration"]
          ? { "text-decoration": questionDefaults["text-decoration"] }
          : {}),
        ...(questionDefaults["text-transform"]
          ? { "text-transform": questionDefaults["text-transform"] }
          : {}),
        ...(questionDefaults["letter-spacing"]
          ? { "letter-spacing": questionDefaults["letter-spacing"] }
          : {}),
        ...(questionDefaults["line-height"] !== undefined
          ? { "line-height": questionDefaults["line-height"] }
          : {}),
      },
      captionTextLeft: "Not at all likely",
      captionTextRight: "Extremely likely",
      captionsAttributes: {
        show: true,
        color: captionsDefaults.color ?? "#9a9a9a",
        "font-size": captionsDefaults["font-size"] ?? "12px",
        "font-family":
          captionsDefaults["font-family"] ?? "Helvetica, Arial, sans-serif",
        padding: captionsDefaults.padding ?? "8px 0px 0px 0px",
        layout: captionsDefaults.layout ?? "below",
        layoutMobile: captionsDefaults.layoutMobile ?? "below",
        ...(captionsDefaults["font-weight"]
          ? { "font-weight": captionsDefaults["font-weight"] }
          : {}),
        ...(captionsDefaults["font-style"]
          ? { "font-style": captionsDefaults["font-style"] }
          : {}),
        ...(captionsDefaults["text-decoration"]
          ? { "text-decoration": captionsDefaults["text-decoration"] }
          : {}),
        ...(captionsDefaults["text-transform"]
          ? { "text-transform": captionsDefaults["text-transform"] }
          : {}),
        ...(captionsDefaults["letter-spacing"]
          ? { "letter-spacing": captionsDefaults["letter-spacing"] }
          : {}),
        ...(captionsDefaults["line-height"] !== undefined
          ? { "line-height": captionsDefaults["line-height"] }
          : {}),
      },
      segmentsAttributes: {
        mode: "text",
        start: START_VALUE,
        count: COUNT,
        size: segmentsDefaults.size ?? "32px",
        gap: segmentsDefaults.gap ?? "4px",
        autoWidth: segmentsDefaults.autoWidth ?? true,
        color: segmentsDefaults.color ?? "#1a1a1a",
        "font-size": segmentsDefaults["font-size"] ?? "14px",
        "font-family":
          segmentsDefaults["font-family"] ?? "Helvetica, Arial, sans-serif",
        "background-color": segmentsDefaults["background-color"] ?? "#ffffff",
        border: segmentsDefaults.border ?? "1px #1a1a1a solid",
        "border-radius": segmentsDefaults["border-radius"] ?? "8px",
        ...(segmentsDefaults["font-weight"]
          ? { "font-weight": segmentsDefaults["font-weight"] }
          : {}),
        ...(segmentsDefaults["font-style"]
          ? { "font-style": segmentsDefaults["font-style"] }
          : {}),
        ...(segmentsDefaults["text-decoration"]
          ? { "text-decoration": segmentsDefaults["text-decoration"] }
          : {}),
        ...(segmentsDefaults["text-transform"]
          ? { "text-transform": segmentsDefaults["text-transform"] }
          : {}),
      },
      imageLabelAttributes: {
        show: false,
        color: imageLabelDefaults.color ?? "#1a1a1a",
        "font-size": imageLabelDefaults["font-size"] ?? "12px",
        "font-family":
          imageLabelDefaults["font-family"] ?? "Helvetica, Arial, sans-serif",
        ...(imageLabelDefaults["font-weight"]
          ? { "font-weight": imageLabelDefaults["font-weight"] }
          : {}),
        ...(imageLabelDefaults["font-style"]
          ? { "font-style": imageLabelDefaults["font-style"] }
          : {}),
        ...(imageLabelDefaults["text-decoration"]
          ? { "text-decoration": imageLabelDefaults["text-decoration"] }
          : {}),
        ...(imageLabelDefaults["text-transform"]
          ? { "text-transform": imageLabelDefaults["text-transform"] }
          : {}),
      },
      url: {
        mode: "pattern",
      },
      urlPattern: "https://example.com/feedback?score={value}",
      children,
    };
  },
};
